<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { usersApi, type User } from '@/services/api/users';
import { useAuthStore } from '@/stores/auth';
import { useNotifier } from '@/composables/notifier';
import AccountSubNav from './AccountSubNav.vue';

const auth = useAuthStore();
const notify = useNotifier();
const user = ref<User | null>(null);
const submitting = ref(false);

onMounted(async () => {
  const id = auth.currentUser?.id;
  if (id !== undefined) {
    user.value = await usersApi.get(id);
  }
});

async function submit(): Promise<void> {
  if (!user.value || submitting.value) return;
  submitting.value = true;
  try {
    await usersApi.update(user.value as User & { id: number });
    user.value = await usersApi.get(user.value.id);
    notify.info('Account updated.');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div>
    <AccountSubNav />
    <form v-if="user" class="account-form" novalidate @submit.prevent="submit">
      <h2 class="account-form__title">Profile</h2>

      <label class="account-form__field" for="profile-title">
        <span>Title</span>
        <input
          id="profile-title"
          v-model="(user as any).title"
          name="title"
          type="text"
          placeholder="Enter your title"
        />
      </label>

      <label class="account-form__field" for="profile-bio">
        <span>Bio</span>
        <textarea
          id="profile-bio"
          v-model="(user as any).bio"
          name="bio"
          rows="5"
          placeholder="Enter your bio"
        />
      </label>

      <button type="submit" :disabled="submitting" class="account-form__submit">
        {{ submitting ? 'Saving…' : 'Update account' }}
      </button>
    </form>
    <p v-else class="account-form__loading">Loading…</p>
  </div>
</template>

<style scoped lang="scss" src="./account-form.scss"></style>
